import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
    ManageSearch as HistoryIcon,
    Notifications as NotificationsIcon,
    Save as SaveIcon,
    Schedule as ScheduleIcon,
} from '@mui/icons-material';

import PhoneInput from '../common/PhoneInput';
import { useNotification } from '../common/GlobalNotifications';
import { api, handleApiError } from '../../services/api';


const CHANNEL_LABELS = {
    portal: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.portal'),
    email: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.email'),
    whatsapp: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.whatsapp'),
    voice: i18n.t('hardcoded.components_chat_calloverlay.llamada'),
    sms: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.sms'),
};

const CHANNEL_ORDER = ['portal', 'email', 'whatsapp', 'voice', 'sms'];
const MATRIX_ORDER = ['alarm', 'critical', 'extreme', 'ticket_updates', 'daily_exception_report', 'weekly_report'];

// Tipos de notificación que se pueden PROBAR (mismo set que la matriz, sin portal-only).
const TEST_TYPE_LABELS = {
    alarm: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.alarma_alerta_atencion'),
    critical: i18n.t('alarmTickets.severity.critical'),
    extreme: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.extrema'),
    ticket_updates: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.actualizaciones_de_ticket'),
    daily_exception_report: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.reporte_diario_por_excepcion'),
    weekly_report: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.reporte_semanal'),
};
const TEST_TYPE_ORDER = ['alarm', 'critical', 'extreme', 'ticket_updates', 'daily_exception_report', 'weekly_report'];
const DAY_OPTIONS = [
    { value: 0, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.lun') },
    { value: 1, label: i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.mar') },
    { value: 2, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.mie') },
    { value: 3, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.jue') },
    { value: 4, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.vie') },
    { value: 5, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.sab') },
    { value: 6, label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.dom') },
];

const DEFAULT_CHANNEL_SETTINGS = {
    portal: { enabled: true },
    email: { enabled: true },
    whatsapp: { enabled: false },
    voice: { enabled: false },
    sms: { enabled: false },
};

const DEFAULT_MATRIX = {
    alarm: { label: i18n.t('hardcoded.components_alarmprofiles_profileform.alerta_atencion'), channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false } },
    critical: { label: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_critica'), channels: { portal: true, email: true, whatsapp: true, voice: true, sms: false } },
    extreme: { label: i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.alarma_extrema'), channels: { portal: true, email: true, whatsapp: true, voice: true, sms: false } },
    ticket_updates: { label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.actualizaciones'), channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false } },
    daily_exception_report: { label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.reporte_diario'), channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false } },
    weekly_report: { label: i18n.t('hardcoded.components_settings_notificationpreferenceseditor.reporte_semanal'), channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false } },
};

const DEFAULT_DAILY_REPORT = {
    enabled: false,
    time: '07:00',
    timezone: 'America/Mexico_City',
    only_if_open_tickets: true,
    channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false },
};

const DEFAULT_WEEKLY_REPORT = {
    enabled: true,
    day_of_week: 0,
    time: '08:00',
    timezone: 'America/Mexico_City',
    include_empty: false,
    channels: { portal: true, email: true, whatsapp: false, voice: false, sms: false },
};

const DEFAULT_CRITICAL_CALL = {
    enabled: true,
    delay_minutes: 5,
    repeat_every_minutes: 5,
    max_attempts: 3,
    channels: { voice: true },
};

const DEFAULT_QUIET_HOURS = {
    enabled: false,
    start: '22:00',
    end: '06:00',
    timezone: 'America/Mexico_City',
};

const normalizeChannels = (value, fallback = {}) => {
    const next = { ...fallback };
    CHANNEL_ORDER.forEach((channel) => {
        next[channel] = Boolean(value?.[channel] ?? next[channel] ?? false);
    });
    return next;
};

const normalizePreferences = (data = {}) => {
    const notificationMatrix = { ...DEFAULT_MATRIX };
    MATRIX_ORDER.forEach((key) => {
        notificationMatrix[key] = {
            label: data?.notification_matrix?.[key]?.label || DEFAULT_MATRIX[key].label,
            channels: normalizeChannels(data?.notification_matrix?.[key]?.channels, DEFAULT_MATRIX[key].channels),
        };
    });

    return {
        ...data,
        channel_settings: Object.fromEntries(
            CHANNEL_ORDER.map((channel) => [
                channel,
                { enabled: Boolean(data?.channel_settings?.[channel]?.enabled ?? DEFAULT_CHANNEL_SETTINGS[channel].enabled) },
            ])
        ),
        notification_matrix: notificationMatrix,
        daily_exception_report: {
            ...DEFAULT_DAILY_REPORT,
            ...data?.daily_exception_report,
            channels: normalizeChannels(data?.daily_exception_report?.channels, DEFAULT_DAILY_REPORT.channels),
        },
        weekly_report: {
            ...DEFAULT_WEEKLY_REPORT,
            ...data?.weekly_report,
            channels: normalizeChannels(data?.weekly_report?.channels, DEFAULT_WEEKLY_REPORT.channels),
        },
        critical_call: {
            ...DEFAULT_CRITICAL_CALL,
            ...data?.critical_call,
            channels: normalizeChannels(data?.critical_call?.channels, DEFAULT_CRITICAL_CALL.channels),
        },
        quiet_hours: {
            ...DEFAULT_QUIET_HOURS,
            ...data?.quiet_hours,
        },
        silence_rules: Array.isArray(data?.silence_rules) ? data.silence_rules.map((rule) => ({
            ...rule,
            channels: normalizeChannels(rule?.channels, { portal: false, email: true, whatsapp: false, voice: false, sms: false }),
            days_of_week: Array.isArray(rule?.days_of_week) ? rule.days_of_week : [0, 1, 2, 3, 4, 5, 6],
            timezone: rule?.timezone || data?.quiet_hours?.timezone || DEFAULT_QUIET_HOURS.timezone,
        })) : [],
    };
};

const NotificationPreferencesEditor = ({
    mode = 'self',
    userId = null,
    userName = '',
    onUpdated,
    allowTesting = mode === 'self',
}) => {
  const { t } = useTranslation();
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [dirty, setDirty] = useState(false);
    // Guardado automático: no hay botón. `editVersionRef` numera cada cambio del
    // usuario para que un guardado en vuelo no marque como limpio algo que se
    // editó después ni sobrescriba lo más reciente con la respuesta del servidor.
    const editVersionRef = useRef(0);
    // Versión que ya falló al guardar. Sin esto, un rechazo del servidor haría
    // que el efecto reintentara cada 700 ms para siempre contra la misma API.
    // Solo se vuelve a intentar cuando el usuario cambia algo más.
    const failedVersionRef = useRef(null);
    const [savedAt, setSavedAt] = useState(null);

    // Diálogo para elegir QUÉ tipo de notificación probar (envía con el formato real).
    const [testDialog, setTestDialog] = useState({ open: false, channels: [], notificationType: 'alarm' });
    const openTestDialog = (channels) => {
        setTestDialog({ open: true, channels, notificationType: 'alarm' });
    };
    const closeTestDialog = () => setTestDialog((p) => ({ ...p, open: false }));
    const { showSuccess, showError } = useNotification();

    const title = useMemo(() => (
        mode === 'admin' ? t('hardcoded.components_settings_notificationpreferenceseditor.notificaciones_de_usernametauditt', { userNametauditt: userName || t('audit.table.user') }) : t('hardcoded.components_settings_notificationpreferenceseditor.preferencias_de_notificacion')
    ), [mode, userName]);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError('');
                const response = mode === 'admin'
                    ? await api.notificationPreferences.getUserPreferences(userId)
                    : await api.notificationPreferences.getMyPreferences();
                if (!mounted) {
                    return;
                }
                setPreferences(normalizePreferences(response.data));
                setDirty(false);
            } catch (err) {
                const apiError = handleApiError(err);
                if (mounted) {
                    setError(apiError.message || t('hardcoded.components_settings_notificationpreferenceseditor.no_se_pudieron_cargar_las'));
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, [mode, userId]);

    const setNextPreferences = (updater) => {
        setPreferences((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            return next;
        });
        // Cada edición avanza la versión. El guardado automático compara contra
        // ella para saber si, mientras escribía en el servidor, el usuario tocó
        // algo más: si es así no se da por guardado ni se pisa lo nuevo.
        editVersionRef.current += 1;
        setDirty(true);
    };

    const updateRootField = (field, value) => {
        setNextPreferences((prev) => ({ ...prev, [field]: value }));
    };

    // Canal encendido sin destinatario utilizable. Es exactamente el caso que en
    // producción se veía como "tengo WhatsApp activado y no me llega nada": el
    // backend lo registra como `invalid_recipient`, y aquí se avisa antes.
    const contactWarnings = useMemo(() => {
        if (!preferences) return [];
        const contacts = preferences.effective_contacts || {};
        const enabled = (channel) => Boolean(preferences.channel_settings?.[channel]?.enabled);
        const warnings = [];
        if (enabled('email') && !contacts.email) {
            warnings.push('Email activo pero el usuario no tiene correo válido');
        }
        if (enabled('whatsapp') && !contacts.whatsapp_phone) {
            warnings.push('WhatsApp activo pero no hay teléfono de WhatsApp válido');
        }
        if (enabled('sms') && !contacts.sms_phone) {
            warnings.push('SMS activo pero no hay teléfono válido');
        }
        if (enabled('voice') && !contacts.voice_phone) {
            warnings.push('Llamada activa pero no hay teléfono válido');
        }
        return warnings;
    }, [preferences]);

    const updateChannelEnabled = (channel, enabled) => {
        setNextPreferences((prev) => ({
            ...prev,
            channel_settings: {
                ...prev.channel_settings,
                [channel]: { enabled },
            },
        }));
    };

    const updateMatrixChannel = (matrixKey, channel, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            notification_matrix: {
                ...prev.notification_matrix,
                [matrixKey]: {
                    ...prev.notification_matrix[matrixKey],
                    channels: {
                        ...prev.notification_matrix[matrixKey].channels,
                        [channel]: value,
                    },
                },
            },
        }));
    };

    const updateScheduleField = (scheduleKey, field, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            [scheduleKey]: {
                ...prev[scheduleKey],
                [field]: value,
            },
        }));
    };

    const updateScheduleChannel = (scheduleKey, channel, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            [scheduleKey]: {
                ...prev[scheduleKey],
                channels: {
                    ...prev[scheduleKey].channels,
                    [channel]: value,
                },
            },
        }));
    };

    const updateQuietHours = (field, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            quiet_hours: {
                ...prev.quiet_hours,
                [field]: value,
            },
        }));
    };

    const addSilenceRule = () => {
        setNextPreferences((prev) => ({
            ...prev,
            silence_rules: [
                ...(prev.silence_rules || []),
                {
                    name: t('hardcoded.components_settings_notificationpreferenceseditor.nueva_regla'),
                    severity: 'alarm',
                    enabled: true,
                    start_time: '22:00',
                    end_time: '06:00',
                    timezone: prev?.quiet_hours?.timezone || DEFAULT_QUIET_HOURS.timezone,
                    days_of_week: [0, 1, 2, 3, 4, 5, 6],
                    channels: { portal: false, email: true, whatsapp: false, voice: false, sms: false },
                    notification_types: ['alarm', 'critical', 'extreme'],
                },
            ],
        }));
    };

    const updateSilenceRule = (index, field, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            silence_rules: (prev.silence_rules || []).map((rule, ruleIndex) => (
                ruleIndex === index ? { ...rule, [field]: value } : rule
            )),
        }));
    };

    const updateSilenceRuleChannel = (index, channel, value) => {
        setNextPreferences((prev) => ({
            ...prev,
            silence_rules: (prev.silence_rules || []).map((rule, ruleIndex) => (
                ruleIndex === index
                    ? {
                        ...rule,
                        channels: {
                            ...rule.channels,
                            [channel]: value,
                        },
                    }
                    : rule
            )),
        }));
    };

    const toggleSilenceDay = (index, dayValue) => {
        setNextPreferences((prev) => ({
            ...prev,
            silence_rules: (prev.silence_rules || []).map((rule, ruleIndex) => {
                if (ruleIndex !== index) {
                    return rule;
                }
                const days = new Set(rule.days_of_week || []);
                if (days.has(dayValue)) {
                    days.delete(dayValue);
                } else {
                    days.add(dayValue);
                }
                return {
                    ...rule,
                    days_of_week: Array.from(days).sort((a, b) => a - b),
                };
            }),
        }));
    };

    const removeSilenceRule = (index) => {
        setNextPreferences((prev) => ({
            ...prev,
            silence_rules: (prev.silence_rules || []).filter((_, ruleIndex) => ruleIndex !== index),
        }));
    };

    const savePreferences = useCallback(async () => {
        if (!preferences) return;
        const versionAtSend = editVersionRef.current;
        try {
            setSaving(true);
            setError('');

            // `enabled` es un interruptor EXPLICITO del usuario, no se deriva de los
            // canales: derivarlo hacia que guardar cualquier preferencia encendiera
            // el reporte sin que nadie lo pidiera. Los canales los gobierna la matriz
            // (backend), asi que aqui solo se manda el on/off elegido.
            const dailyReport = {
                ...preferences.daily_exception_report,
                enabled: Boolean(preferences.daily_exception_report?.enabled),
            };
            const weeklyReport = {
                ...preferences.weekly_report,
                enabled: Boolean(preferences.weekly_report?.enabled),
            };

            const payload = {
                email_address: preferences.email_address,
                sms_phone: preferences.sms_phone,
                whatsapp_phone: preferences.whatsapp_phone,
                channel_settings: preferences.channel_settings,
                notification_matrix: preferences.notification_matrix,
                quiet_hours: preferences.quiet_hours,
                daily_exception_report: dailyReport,
                weekly_report: weeklyReport,
                critical_call: preferences.critical_call,
                silence_rules: preferences.silence_rules,
            };

            const response = mode === 'admin'
                ? await api.notificationPreferences.updateUserPreferences(userId, payload)
                : await api.notificationPreferences.updateMyPreferences(payload);

            const normalized = normalizePreferences(response.data);
            if (editVersionRef.current === versionAtSend) {
                // Nadie tocó nada mientras se guardaba: es seguro adoptar la
                // respuesta del servidor y darlo por guardado.
                setPreferences(normalized);
                setDirty(false);
                setSavedAt(new Date());
            } else {
                // Hubo ediciones en vuelo: se conservan y el efecto las guardará
                // en la siguiente pasada. Pisar aquí perdería el último clic.
                setSavedAt(new Date());
            }
            if (typeof onUpdated === 'function') {
                onUpdated(normalized);
            }
        } catch (err) {
            const apiError = handleApiError(err);
            const message = apiError.message || t('hardcoded.components_settings_notificationpreferenceseditor.no_se_pudieron_guardar_las');
            // El error se muestra fijo junto a la cabecera Y como aviso: con
            // guardado automático no hay botón que delate que algo no se guardó.
            setError(message);
            showError(message);
            failedVersionRef.current = versionAtSend;
        } finally {
            setSaving(false);
        }
    }, [preferences, mode, userId, onUpdated, showError, t]);

    // Autoguardado con espera: los interruptores se pulsan en ráfaga (la matriz
    // tiene decenas), así que se agrupa el cambio en vez de lanzar una petición
    // por clic. Si falla, `dirty` sigue en true y se reintenta al siguiente cambio.
    useEffect(() => {
        if (!dirty || saving || !preferences) return undefined;
        if (failedVersionRef.current === editVersionRef.current) return undefined;
        const timer = setTimeout(() => { savePreferences(); }, 700);
        return () => clearTimeout(timer);
    }, [dirty, saving, preferences, savePreferences]);

    const runChannelTest = async (channels, notificationType) => {
        if (!notificationType) {
            showError(t('hardcoded.components_settings_notificationpreferenceseditor.selecciona_el_tipo_de_notificacion'));
            return;
        }
        try {
            setTesting(true);
            // La prueba la resuelve el backend con las preferencias GUARDADAS. Con
            // autoguardado puede haber un cambio recién hecho aún en la espera de
            // 700 ms, así que se confirma antes: si no, se probaría el destinatario
            // anterior y el resultado no diría nada útil.
            if (dirty) {
                await savePreferences();
            }
            await api.notificationPreferences.testNotifications({ channels, notification_type: notificationType });
            showSuccess(t('hardcoded.components_settings_notificationpreferenceseditor.prueba_test_type_label_enviada_por_channelsjoin', { TEST_TYPE_LABEL: TEST_TYPE_LABELS[notificationType] || notificationType, channelsjoin: channels.join(', ') }));
            closeTestDialog();
        } catch (err) {
            const apiError = handleApiError(err);
            showError(apiError.message || t('hardcoded.components_settings_notificationpreferenceseditor.no_se_pudo_ejecutar_la'));
        } finally {
            setTesting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, gap: 2 }}>
                <CircularProgress />
                <Typography>{t('hardcoded.components_settings_notificationpreferenceseditor.cargando_preferencias_de_notificacion')}</Typography>
            </Box>
        );
    }

    if (!preferences) {
        return <Alert severity="warning">{t('hardcoded.components_settings_notificationpreferenceseditor.no_se_pudieron_cargar_las')}</Alert>;
    }

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('hardcoded.components_settings_notificationpreferenceseditor.configura_canales_por_severidad_reportes')}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Sin botón de guardar: los cambios se escriben solos. El
                        estado se muestra siempre para que nunca haya duda de si
                        quedó guardado, que es lo único que aportaba el botón. */}
                    <Chip
                        size="small"
                        variant={saving || dirty ? 'filled' : 'outlined'}
                        color={error ? 'error' : (saving || dirty ? 'warning' : 'success')}
                        icon={saving ? <CircularProgress size={12} sx={{ ml: 1 }} /> : <SaveIcon />}
                        // En error el chip reintenta: sin botón de guardar, un fallo
                        // dejaría al usuario sin forma de volver a intentarlo salvo
                        // tocando otra opción al azar.
                        onClick={error && !saving ? () => {
                            failedVersionRef.current = null;
                            savePreferences();
                        } : undefined}
                        label={
                            error ? 'No se guardó · reintentar'
                                : saving ? 'Guardando…'
                                    : dirty ? 'Guardando cambios…'
                                        : savedAt
                                            ? `Guardado ${savedAt.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`
                                            : 'Guardado automático'
                        }
                    />
                    {/* Atajo al Centro de Notificaciones ya filtrado por este usuario:
                        la pregunta "¿le llegó o no?" se responde con la evidencia real,
                        no releyendo los toggles de esta pantalla. */}
                    {mode === 'admin' && userId && (
                        <Button
                            variant="outlined"
                            startIcon={<HistoryIcon />}
                            onClick={() => window.open(`/admin/notification-audit?user_id=${userId}`, '_blank')}
                        >
                            Ver auditoría de envíos
                        </Button>
                    )}
                    {allowTesting && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<EmailIcon />}
                                onClick={() => openTestDialog(['email'])}
                                disabled={testing}
                            >
                                {i18n.t('hardcoded.components_settings_notificationpreferenceseditor.probar_email')}</Button>
                            <Button
                                variant="outlined"
                                onClick={() => openTestDialog(['whatsapp'])}
                                disabled={testing}
                            >
                                Probar WhatsApp
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} lg={5}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <NotificationsIcon color="primary" />
                                <Typography variant="h6">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.canales_y_contactos')}</Typography>
                            </Box>

                            <FormGroup>
                                {CHANNEL_ORDER.map((channel) => (
                                    <FormControlLabel
                                        key={channel}
                                        control={
                                            <Switch
                                                checked={Boolean(preferences.channel_settings?.[channel]?.enabled)}
                                                onChange={(event) => updateChannelEnabled(channel, event.target.checked)}
                                            />
                                        }
                                        label={CHANNEL_LABELS[channel]}
                                    />
                                ))}
                            </FormGroup>

                            {contactWarnings.length > 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                                        Estos canales están activos pero no pueden entregar
                                    </Typography>
                                    {contactWarnings.map((warning) => (
                                        <Typography key={warning} variant="caption" component="div">• {warning}</Typography>
                                    ))}
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                        Se puede guardar igual, pero esas notificaciones quedarán
                                        registradas como «sin contacto válido» en vez de enviarse.
                                    </Typography>
                                </Alert>
                            )}

                            <Divider sx={{ my: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={t('hardcoded.components_settings_notificationpreferenceseditor.email_alternativo')}
                                        value={preferences.email_address || ''}
                                        onChange={(event) => updateRootField('email_address', event.target.value)}
                                        helperText={t('hardcoded.components_settings_notificationpreferenceseditor.actual_preferenceseffe', { preferenceseffe: preferences.effective_contacts?.email || t('hardcoded.components_settings_companynetworkstab.sin_correo') })}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <PhoneInput
                                        value={preferences.sms_phone || ''}
                                        onChange={(value) => updateRootField('sms_phone', value)}
                                        label={t('hardcoded.components_settings_notificationpreferenceseditor.telefono_smsllamada')}
                                        helperText={t('hardcoded.components_settings_notificationpreferenceseditor.actual_preferenceseffe', { preferenceseffe: preferences.effective_contacts?.voice_phone || t('hardcoded.components_operators_operatorcontactmenu.sin_telefono') })}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <PhoneInput
                                        value={preferences.whatsapp_phone || ''}
                                        onChange={(value) => updateRootField('whatsapp_phone', value)}
                                        label={t('hardcoded.components_settings_notificationpreferenceseditor.telefono_whatsapp')}
                                        helperText={t('hardcoded.components_settings_notificationpreferenceseditor.actual_preferenceseffe', { preferenceseffe: preferences.effective_contacts?.whatsapp_phone || t('hardcoded.components_operators_operatorcontactmenu.sin_telefono') })}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" gutterBottom>
                                {i18n.t('hardcoded.components_settings_notificationpreferenceseditor.quiet_hours_legacy')}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(preferences.quiet_hours?.enabled)}
                                        onChange={(event) => updateQuietHours('enabled', event.target.checked)}
                                    />
                                }
                                label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.activar_quiet_hours')}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="time"
                                        size="small"
                                        label={t('hardcoded.components_settings_notificationpreferenceseditor.inicio')}
                                        value={preferences.quiet_hours?.start || '22:00'}
                                        onChange={(event) => updateQuietHours('start', event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        type="time"
                                        size="small"
                                        label="Fin"
                                        value={preferences.quiet_hours?.end || '06:00'}
                                        onChange={(event) => updateQuietHours('end', event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.timezone')}
                                        value={preferences.quiet_hours?.timezone || DEFAULT_QUIET_HOURS.timezone}
                                        onChange={(event) => updateQuietHours('timezone', event.target.value)}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('hardcoded.components_settings_notificationpreferenceseditor.notificaciones_por_tipo')}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('hardcoded.components_settings_notificationpreferenceseditor.activa_las_casillas_de_los')}<strong>{i18n.t('hardcoded.components_ai_aimemoryeditor.activa')}</strong> {t('hardcoded.components_settings_notificationpreferenceseditor.automaticamente_si_al_menos_un')}</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.evento')}</TableCell>
                                            {CHANNEL_ORDER.map((channel) => (
                                                <TableCell key={channel} align="center">{CHANNEL_LABELS[channel]}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {MATRIX_ORDER.map((matrixKey) => (
                                            <TableRow key={matrixKey}>
                                                <TableCell>
                                                    <Typography fontWeight={600}>{preferences.notification_matrix?.[matrixKey]?.label}</Typography>
                                                </TableCell>
                                                {CHANNEL_ORDER.map((channel) => (
                                                    <TableCell key={`${matrixKey}-${channel}`} align="center">
                                                        <Checkbox
                                                            checked={Boolean(preferences.notification_matrix?.[matrixKey]?.channels?.[channel])}
                                                            onChange={(event) => updateMatrixChannel(matrixKey, channel, event.target.checked)}
                                                            disabled={!preferences.channel_settings?.[channel]?.enabled && channel !== 'portal'}
                                                        />
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <ScheduleIcon color="primary" />
                                <Typography variant="h6">{t('hardcoded.components_settings_notificationpreferenceseditor.reportes_y_escalada_critica')}</Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={700}>{t('hardcoded.components_settings_notificationpreferenceseditor.reporte_diario_por_excepcion')}</Typography>
                                            <Switch
                                                size="small"
                                                checked={Boolean(preferences.daily_exception_report?.enabled)}
                                                onChange={(event) => updateScheduleField(
                                                    'daily_exception_report', 'enabled', event.target.checked)}
                                                inputProps={{ 'aria-label': 'Activar reporte diario' }}
                                            />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                            Se envía si este interruptor está encendido y el canal está marcado en <strong>Notificaciones por tipo</strong>.
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            size="small"
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.hora')}
                                            value={preferences.daily_exception_report?.time || '07:00'}
                                            onChange={(event) => updateScheduleField('daily_exception_report', 'time', event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ mt: 1 }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.timezone')}
                                            value={preferences.daily_exception_report?.timezone || DEFAULT_DAILY_REPORT.timezone}
                                            onChange={(event) => updateScheduleField('daily_exception_report', 'timezone', event.target.value)}
                                            sx={{ mt: 2 }}
                                        />
                                        <FormControlLabel
                                            sx={{ mt: 1 }}
                                            control={
                                                <Checkbox
                                                    checked={Boolean(preferences.daily_exception_report?.only_if_open_tickets)}
                                                    onChange={(event) => updateScheduleField('daily_exception_report', 'only_if_open_tickets', event.target.checked)}
                                                />
                                            }
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.solo_si_quedaron_tickets_abiertos')}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={700}>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.reporte_semanal')}</Typography>
                                            <Switch
                                                size="small"
                                                checked={Boolean(preferences.weekly_report?.enabled)}
                                                onChange={(event) => updateScheduleField(
                                                    'weekly_report', 'enabled', event.target.checked)}
                                                inputProps={{ 'aria-label': 'Activar reporte semanal' }}
                                            />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                            Se envía si este interruptor está encendido y el canal está marcado en <strong>Notificaciones por tipo</strong>.
                                        </Typography>
                                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                            <InputLabel>{t('hardcoded.components_settings_notificationpreferenceseditor.dia')}</InputLabel>
                                            <Select
                                                value={preferences.weekly_report?.day_of_week ?? 0}
                                                label={t('hardcoded.components_settings_notificationpreferenceseditor.dia')}
                                                onChange={(event) => updateScheduleField('weekly_report', 'day_of_week', Number(event.target.value))}
                                            >
                                                {DAY_OPTIONS.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            type="time"
                                            size="small"
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.hora')}
                                            value={preferences.weekly_report?.time || '08:00'}
                                            onChange={(event) => updateScheduleField('weekly_report', 'time', event.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ mt: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.timezone')}
                                            value={preferences.weekly_report?.timezone || DEFAULT_WEEKLY_REPORT.timezone}
                                            onChange={(event) => updateScheduleField('weekly_report', 'timezone', event.target.value)}
                                            sx={{ mt: 2 }}
                                        />
                                        <FormControlLabel
                                            sx={{ mt: 1 }}
                                            control={
                                                <Checkbox
                                                    checked={Boolean(preferences.weekly_report?.include_empty)}
                                                    onChange={(event) => updateScheduleField('weekly_report', 'include_empty', event.target.checked)}
                                                />
                                            }
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.enviar_aunque_no_haya_actividad')}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(preferences.critical_call?.enabled)}
                                                    onChange={(event) => updateScheduleField('critical_call', 'enabled', event.target.checked)}
                                                />
                                            }
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.llamada_critica_persistente')}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.delay_inicial_min')}
                                            value={preferences.critical_call?.delay_minutes ?? 5}
                                            onChange={(event) => updateScheduleField('critical_call', 'delay_minutes', Number(event.target.value))}
                                            sx={{ mt: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.reintento_cada_min')}
                                            value={preferences.critical_call?.repeat_every_minutes ?? 5}
                                            onChange={(event) => updateScheduleField('critical_call', 'repeat_every_minutes', Number(event.target.value))}
                                            sx={{ mt: 2 }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            label={t('hardcoded.components_settings_notificationpreferenceseditor.maximo_de_intentos')}
                                            value={preferences.critical_call?.max_attempts ?? 3}
                                            onChange={(event) => updateScheduleField('critical_call', 'max_attempts', Number(event.target.value))}
                                            sx={{ mt: 2 }}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography variant="h6">{t('hardcoded.components_settings_notificationpreferenceseditor.reglas_de_silencio')}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('hardcoded.components_settings_notificationpreferenceseditor.define_ventanas_por_severidad_canal')}</Typography>
                                </Box>
                                <Button variant="outlined" startIcon={<AddIcon />} onClick={addSilenceRule}>
                                    {i18n.t('hardcoded.components_settings_notificationpreferenceseditor.agregar_regla')}</Button>
                            </Box>

                            {(preferences.silence_rules || []).length === 0 && (
                                <Alert severity="info">{t('hardcoded.components_settings_notificationpreferenceseditor.no_hay_reglas_de_silencio')}</Alert>
                            )}

                            {(preferences.silence_rules || []).map((rule, index) => (
                                <Paper key={`${rule.id || 'new'}-${index}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography fontWeight={600}>{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.regla')}{index + 1}</Typography>
                                        <IconButton color="error" onClick={() => removeSilenceRule(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.nombre')}
                                                value={rule.name || ''}
                                                onChange={(event) => updateSilenceRule(index, 'name', event.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>{t('hardcoded.components_settings_notificationpreferenceseditor.severidad')}</InputLabel>
                                                <Select
                                                    value={rule.severity || 'alarm'}
                                                    label={t('hardcoded.components_settings_notificationpreferenceseditor.severidad')}
                                                    onChange={(event) => updateSilenceRule(index, 'severity', event.target.value)}
                                                >
                                                    <MenuItem value="alarm">{i18n.t('alarmTickets.severity.alarm')}</MenuItem>
                                                    <MenuItem value="critical">{t('alarmTickets.severity.critical')}</MenuItem>
                                                    <MenuItem value="extreme">Extrema</MenuItem>
                                                    <MenuItem value="all">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="time"
                                                label={t('hardcoded.components_settings_notificationpreferenceseditor.inicio')}
                                                value={rule.start_time || '22:00'}
                                                onChange={(event) => updateSilenceRule(index, 'start_time', event.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="time"
                                                label="Fin"
                                                value={rule.end_time || '06:00'}
                                                onChange={(event) => updateSilenceRule(index, 'end_time', event.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={1}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={Boolean(rule.enabled)}
                                                        onChange={(event) => updateSilenceRule(index, 'enabled', event.target.checked)}
                                                    />
                                                }
                                                label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.on')}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label={i18n.t('hardcoded.components_settings_notificationpreferenceseditor.timezone')}
                                                value={rule.timezone || DEFAULT_QUIET_HOURS.timezone}
                                                onChange={(event) => updateSilenceRule(index, 'timezone', event.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                {t('hardcoded.components_settings_notificationpreferenceseditor.dias_activos')}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {DAY_OPTIONS.map((day) => (
                                                    <Chip
                                                        key={`${index}-${day.value}`}
                                                        label={day.label}
                                                        color={(rule.days_of_week || []).includes(day.value) ? 'primary' : 'default'}
                                                        variant={(rule.days_of_week || []).includes(day.value) ? 'filled' : 'outlined'}
                                                        onClick={() => toggleSilenceDay(index, day.value)}
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormGroup row>
                                                {CHANNEL_ORDER.filter((channel) => channel !== 'portal').map((channel) => (
                                                    <FormControlLabel
                                                        key={`${index}-${channel}`}
                                                        control={
                                                            <Checkbox
                                                                checked={Boolean(rule.channels?.[channel])}
                                                                onChange={(event) => updateSilenceRuleChannel(index, channel, event.target.checked)}
                                                            />
                                                        }
                                                        label={CHANNEL_LABELS[channel]}
                                                    />
                                                ))}
                                            </FormGroup>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Dialogo: elegir QUÉ tipo de notificación probar (envía con el formato real) */}
            <Dialog open={testDialog.open} onClose={closeTestDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {i18n.t('hardcoded.components_settings_notificationpreferenceseditor.probar')}{testDialog.channels.includes('whatsapp') ? i18n.t('hardcoded.components_settings_notificationpreferenceseditor.whatsapp') : i18n.t('hardcoded.components_settings_notificationpreferenceseditor.email')}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            {t('hardcoded.components_settings_notificationpreferenceseditor.selecciona_el_tipo_de_notificacion')}<strong>{testDialog.channels.join(', ')}</strong> {t('hardcoded.components_settings_notificationpreferenceseditor.con_el_formato_real_de')}</Typography>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_settings_notificationpreferenceseditor.tipo_de_notificacion')}</InputLabel>
                            <Select
                                label={t('hardcoded.components_settings_notificationpreferenceseditor.tipo_de_notificacion')}
                                value={testDialog.notificationType}
                                onChange={(e) => setTestDialog((p) => ({ ...p, notificationType: e.target.value }))}
                            >
                                {TEST_TYPE_ORDER.map((t) => (
                                    <MenuItem key={t} value={t}>{TEST_TYPE_LABELS[t]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeTestDialog} disabled={testing}>{t('common.cancel')}</Button>
                    <Button
                        variant="contained"
                        disabled={testing || !testDialog.notificationType}
                        startIcon={testing ? <CircularProgress size={14} /> : <NotificationsIcon />}
                        onClick={() => runChannelTest(testDialog.channels, testDialog.notificationType)}
                    >
                        {t('hardcoded.components_settings_notificationpreferenceseditor.enviar_prueba')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};


export default NotificationPreferencesEditor;